import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {COMPLETED, IN_PROGRESS, WAITING, ERROR} from "../code-importation-dialog/code-importation-dialog.component";
import {FormTemplateService} from "../../../../../../lib/services/form-template.service";
import {FormTemplateType, ProgressInfos} from "../../../../../../lib/models/formTemplate.model";
import {FormTemplateTypeEnum} from "../../../../../../lib/models/formTemplate.model";
import {GROUP_ID_TEMPLATE} from "../../../../../../lib/services/tools.service";
import {FormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {FormTemplate} from "@icure/api";

interface TemplateCategory {
  key: string;
  label: string;
  specialtyCode: FormTemplateTypeEnum;
}

const TEMPLATE_CATEGORIES: TemplateCategory[] = [
  {
    key: 'deptGeneralPractice',
    label: "Département de médecine générale",
    specialtyCode: FormTemplateTypeEnum.DEPT_GENERAL_PRACTICE
  },
  {
    key: 'orgRevalidationCenter',
    label: "Centre de revalidation",
    specialtyCode: FormTemplateTypeEnum.ORG_REVALIDATION_CENTER
  },
  {
    key: 'deptPhysiotherapy',
    label: 'Département de kinésithérapie',
    specialtyCode: FormTemplateTypeEnum.DEPT_PHYSIOTHERAPY
  },
];

@Component({
  selector: 'ms-template-importation-dialog',
  imports: [FormsModule, NgClass],
  templateUrl: './template-importation-dialog.component.html',
  styleUrl: './template-importation-dialog.component.scss'
})
export class TemplateImportationDialogComponent implements OnInit {
  public isLoading: boolean = false;
  public isImporting: boolean = false;
  public isLoadingTemplates: boolean = true;

  public categories: TemplateCategory[] = TEMPLATE_CATEGORIES;
  public templatesByCategory: Record<string, FormTemplateType[]> = {};
  private sourceTemplatesByCategory: Record<string, FormTemplate[]> = {};

  protected readonly COMPLETED = COMPLETED;
  protected readonly WAITING = WAITING;
  protected readonly IN_PROGRESS = IN_PROGRESS;
  protected readonly ERROR = ERROR;

  constructor(
    public dialogRef: MatDialogRef<TemplateImportationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formTemplateService: FormTemplateService
  ) {
    dialogRef.disableClose = true;
  }

  public async ngOnInit(): Promise<void> {
    this.isLoadingTemplates = true;
    try {
      for (const category of this.categories) {
        const formTemplates = await this.formTemplateService.getFormTemplatesBySpecialtyInGroup(
          GROUP_ID_TEMPLATE,
          [category.specialtyCode]
        );

        this.sourceTemplatesByCategory[category.key] = formTemplates;

        this.templatesByCategory[category.key] = formTemplates.map(ft => ({
          id: ft.id ?? '',
          name: ft.name ?? ft.id ?? '',
          category: category.key,
          active: true,
          selected: true,
          progressInfos: {
            injectedCode: 0,
            updatedCode: 0,
            status: 'WAITING' as const
          }
        }));
      }

      console.log(this.templatesByCategory)
    } catch (e) {
      console.log("Erreur lors du chargement des templates des template: ", e);
    }


    this.isLoadingTemplates = false;
  }

  public closeDialog(): void {
    this.isLoading = false;
    this.isImporting = false;
    this.dialogRef.close();
  }

  public checkAll(checked: boolean, categoryKey: string): void {
    this.templatesByCategory[categoryKey] = this.templatesByCategory[categoryKey]?.map(t => ({
      ...t,
      selected: checked
    }));
  }

  public allTemplatesChecked(categoryKey: string): boolean {
    const templates = this.templatesByCategory[categoryKey];
    if (!templates?.length) return false;
    return templates.filter(t => t.active).length === templates.filter(t => t.selected && t.active).length;
  }

  public async importSelectedTemplates(): Promise<void> {
    this.isLoading = true;
    this.isImporting = true;

    for (const category of this.categories) {
      const selected = this.templatesByCategory[category.key]?.filter(t => t.selected) ?? [];
      for (const templateType of selected) {
        this.updateProgressInfo(category.key, templateType.id, 0, 0, IN_PROGRESS);

        const sourceTemplate = this.sourceTemplatesByCategory[category.key]
          ?.find(ft => ft.id === templateType.id);

        if (sourceTemplate) {
          try {
            const infos = await this.formTemplateService.injectFormTemplateIntoDb(
              this.data.selectedGroup.id,
              sourceTemplate?.id!!
            );
            this.updateProgressInfo(category.key, templateType.id, infos.injectedFormTemplate, infos.updatedFormTemplate, infos.status);
          } catch {
            this.updateProgressInfo(category.key, templateType.id, 0, 0, ERROR);
          }
        } else {
          this.updateProgressInfo(category.key, templateType.id, 0, 0, ERROR);
        }
      }
    }

    this.isLoading = false;
  }

  private updateProgressInfo(categoryKey: string, templateId: string, injected: number, updated: number, status: string): void {
    this.templatesByCategory[categoryKey] = this.templatesByCategory[categoryKey].map(t =>
      t.id === templateId
        ? {
          ...t,
          progressInfos: {injectedCode: injected, updatedCode: updated, status: status as ProgressInfos['status']}
        }
        : t
    );
  }

  public getStatusHr(status: string): string {
    switch (status) {
      case WAITING:
        return 'En attente';
      case IN_PROGRESS:
        return 'En cours';
      case COMPLETED:
        return 'Terminé';
      case ERROR:
        return 'En erreur';
      default:
        return '';
    }
  }

  public getCounterByStatus(statuses: string[]): number {
    return this.categories.reduce((count, cat) => {
      const templates = this.templatesByCategory[cat.key] ?? [];
      return count + templates.filter(t => t.selected && statuses.includes(t.progressInfos.status)).length;
    }, 0);
  }

  public getTotalCounter(): number {
    return this.categories.reduce((count, cat) => {
      const templates = this.templatesByCategory[cat.key] ?? [];
      return count + templates.filter(t => t.selected).length;
    }, 0);
  }

  public translateFormTemplateName(templateName: string): string {
    switch (templateName) {
      case "physiotherapy-form.physiotherapy.physicalExamen":
        return "Examen physique";
      case "physiotherapy-form.physiotherapy.clinicalDecision":
        return "Décision clinique";
      case "physiotherapy-form.physiotherapy.uro.abnominoSpinalAssessment":
        return "(URO) Examen de l'abnomino-spinal";
      case "physiotherapy-form.physiotherapy.subjectiveExamen":
        return "Examen subjectif";
      case "physiotherapy-form.physiotherapy.uro.miscellaneous":
        return "(URO) Divers";
      case "physiotherapy-form.physiotherapy.uro.imperiousness":
        return "(URO) Imperieux";
      case "physiotherapy-form.physiotherapy.uro.transit":
        return "(URO) Transit";
      case "physiotherapy-form.physiotherapy.dailyFollowUp":
        return "Suivi quotidien";
      case "physiotherapy-form.physiotherapy.uro.prolapse":
        return "(URO) Prolapsus";
      case "physiotherapy-form.physiotherapy.uro.urinaryLeakage":
        return "(URO) Incontinence urinaire";
      case "physiotherapy-form.physiotherapy.uro.anorectalLeak":
        return "(URO) Incontinence anorectale";
      case "physiotherapy-form.physiotherapy.uro.sexuality":
        return "(URO) Sexualité";
      case "physiotherapy-form.physiotherapy.assessmentFull":
        return "Rapport complet";
      case "physiotherapy-form.physiotherapy.closure":
        return "Rapport de clôture";
      case "physiotherapy-form.okido.okido":
        return "Formulaire OKIDO";
      case "abortion-forms.TERMINATION-OF-PREGNANCY-CURETTAGE":
        return "Interruption de grossesse - Curettage";
      case "abortion-forms.NOTE":
        return "Note";
      case "abortion-forms.PRELIMINARY-PSYCHO-SOCIAL-INTERVIEW":
        return "Entretien psychosocial préliminaire";
      case "abortion-forms.INTERRUPTION-OF-PREGNANCY-MEDICAL-PART-1":
        return "Interruption de grossesse - Partie médicale 1";
      case "abortion-forms.PRELIMINARY-MEDICAL-INTERVIEW":
        return "Entretien médical préliminaire";
      case "abortion-forms.EXTRA":
        return "Extra";
      case "abortion-forms.INTERRUPTION-OF-PREGNANCY-MEDICAL-PART-2":
        return "Interruption de grossesse - Partie médicale 2";
      case "abortion-forms.CONTROL":
        return "Examen de contrôle";
      case "abortion-forms.TIME-OF-APPOINTMENT":
        return "Heure d'échéance";
      default:
        return templateName;
    }
  }
}
